import React from "react";
import { FaStar, FaRegClock } from "react-icons/fa";
import { Link } from "react-router-dom";
import FoodImg from "../../assets/images/restaurant/_1.png";
import PlaceholderImg from "../../assets/images/new_ui/placeholder.jpg";
import { FoodCard } from "./FoodCard";

const parseTime = (timeString) => {
  if (!timeString) return null;
  const parts = timeString.trim().split(" ");
  let hours = 0;
  let minutes = 0;
  if (parts.length === 2) {
    const [h, m] = parts[0].split(":").map(Number);
    hours = h;
    minutes = m;
    const period = parts[1].toUpperCase();
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
  } else {
    const [h, m] = parts[0].split(":").map(Number);
    hours = h;
    minutes = m;
  }
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const formatTime12h = (timeString) => {
  if (!timeString) return "";
  const parts = timeString.trim().split(" ");
  if (parts.length === 2) return timeString;
  const [h, m] = parts[0].split(":").map(Number);
  if (isNaN(h)) return timeString;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const mStr = (m < 10 ? `0${m}` : `${m}`) || "00";
  return `${h12}:${mStr} ${period}`;
};

const RestaurantCard = ({ restaurant, businessType }) => {
  const now = new Date();
  const lagosDay = new Intl.DateTimeFormat("en-US", {
    timeZone: "Africa/Lagos",
    weekday: "short",
  }).format(now);
  const isSunday = lagosDay === "Sun";

  const isSundayClosed = isSunday && restaurant.isSundayOpen === false;

  const rawOpenTime = isSunday
    ? restaurant.sundayOpenTime || restaurant.openTime
    : restaurant.openTime;
  const rawCloseTime = isSunday
    ? restaurant.sundayCloseTime || restaurant.closeTime
    : restaurant.closeTime;

  const openTime = parseTime(rawOpenTime);
  let closeTime = parseTime(rawCloseTime);

  if (closeTime && openTime && closeTime < openTime) {
    closeTime = new Date(closeTime);
    closeTime.setDate(closeTime.getDate() + 1);
  }

  const isBeforeOpening = openTime && now < openTime;
  const displayOpenTime = formatTime12h(rawOpenTime);

  let statusMessage = "CLOSED";
  if (restaurant.isOpen) {
    statusMessage = "OPEN NOW";
  } else if (isSundayClosed) {
    statusMessage = `CLOSED SUNDAYS (OPENS MONDAY AT ${formatTime12h(restaurant.openTime) || "9:00 AM"})`;
  } else if (isBeforeOpening) {
    statusMessage = `OPENS TODAY AT ${displayOpenTime}`;
  } else {
    statusMessage = `OPENS TOMORROW AT ${formatTime12h(restaurant.openTime || "9:00 AM")}`;
  }

  const isRawFood = businessType === "raw_food";
  const isStore = businessType === "store";

  const showGrayScale = () => {
    return false;
  };

  const vendorId =
    restaurant?.business?.id || restaurant?.businessId || restaurant?.id;

  const fallbackImg =
    businessType === "restaurant" ? FoodImg : PlaceholderImg;

  const cardContent = (
    <>
      <div className="relative">
        <img
          src={
            restaurant.bannerImage ??
            restaurant?.business?.bannerImage ??
            fallbackImg
          }
          alt="promo"
          className="w-full h-64 object-cover rounded-t-lg"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = fallbackImg;
          }}
        />
        {businessType === "restaurant" && (
          <div className="absolute top-6 left-4 bg-black/90 text-white text-lg px-3 py-1.5 rounded-md uppercase font-semibold tracking-wide">
            {statusMessage}
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="flex justify-between items-start">
          <h3 className="text-4xl font-medium text-gray-900">
            {restaurant.name ?? restaurant?.business?.name}
          </h3>
          {restaurant.rating !== undefined && restaurant.rating !== null && (
            <div className="flex items-center gap-1">
              <FaStar className="text-[#f59e0b] text-xl" />
              <span className="text-xl font-medium text-gray-900">
                {restaurant.rating}
              </span>
            </div>
          )}
        </div>

        <div className="mt-1 flex flex-wrap gap-1">
          {restaurant.cuisines?.map((cuisine, index) => (
            <span
              key={index}
              className="text-xs text-gray-500 font-medium capitalize"
            >
              {cuisine}
            </span>
          ))}
        </div>

        <div className="flex justify-between items-center">
          {restaurant.minReadyTime && (
            <div className="mt-2 flex items-center gap-2">
              <FaRegClock className="text-gray-600 text-lg" />
              <span className="text-lg font-medium text-gray-900">
                {restaurant.minReadyTime} mins
              </span>
            </div>
          )}
          <p className="text-lg font-medium text-gray-900">
            {restaurant?.pickupAddressString ??
              restaurant?.supplyAddress ??
              restaurant?.business?.address ??
              (restaurant?.lga && restaurant?.state
                ? `${restaurant.lga}, ${restaurant.state}`
                : restaurant?.lga || restaurant?.state || "")}
          </p>
        </div>
      </div>
    </>
  );

  const baseClassName = `block border border-solid border-[#e5e7eb] rounded-lg mb-4 hover:shadow-md transition-shadow duration-200 bg-white relative ${
    showGrayScale() ? "grayscale" : ""
  }`;

  return (
    <Link to={`/${businessType}/${vendorId}`} className={baseClassName}>
      {cardContent}
    </Link>
  );
};

export default RestaurantCard;
